// Detail.js — DROP-IN (self-hydrating by ID; no conditional hooks; no other file changes needed)

import React, { useState, useEffect, useRef, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import moment from "moment-timezone";
import { ResponsiveCalendar } from "@nivo/calendar";
import "moment-precise-range-plugin";
import { updateAlertVehicle } from "./data"
import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-calendar-heatmap/dist/styles.css";

import logos from "./Assets/logos.json";
import calendar from "./Assets/calendar.svg";
import clock from "./Assets/clock.svg";
import crane from "./Assets/crane.svg";
import age from "./Assets/old.svg";
import engine from "./Assets/engine.svg";
import tax from "./Assets/tax.svg";
import car from "./Assets/car.svg";
import paint from "./Assets/paint.svg";
import direction from "./Assets/direction.svg";
import firstSeen from "./Assets/firstSeen.svg";
import lastSeen from "./Assets/lastSeen.svg";
import cylinder from "./Assets/cylinder.svg";
import emission from "./Assets/emission.svg";
import lastV5 from "./Assets/lastV5.svg"


import {
  API,
  fetchMotTests,
  getHitCount,
  checkAlertVehicle,
  addAlertVehicle,
  removeAlertVehicle,
  listAlertVehicles,
  listUserTags,
  createUserTag,
  addUserTagToVehicle,
  normalizeKey,
  fetchVehicleTags,
  removeUserTagFromVehicle,
  getAlertTypesSnapshot,
  refreshAlertTypes,
  fetchHitById
} from "./data";

import { getFirstReg, getAge } from "./utils/vehicle";


/* ---------- small helpers ---------- */
const severityToClass = (s = "normal") => (s === "severe" ? "bad" : s === "warning" ? "unk" : "ok");
const capitalizeFirstLetter = (str = "") => (str ? str[0].toUpperCase() + str.slice(1) : "");
function prettyRecentDate(m, tz = "Europe/London") {
  const local = m.clone().tz(tz);
  const now = moment.tz(tz);
  const startOfToday = now.clone().startOf("day");
  const startOfYear = now.clone().startOf("year");
  const startOfTarget = local.clone().startOf("day");
  const diffDays = startOfToday.diff(startOfTarget, "days");
  if (diffDays === 0) return `Today, ${local.format("HH:mm")}`;
  if (diffDays === 1) return `Yesterday, ${local.format("HH:mm")}`;
  if (diffDays >= 2 && diffDays <= 6) return local.format("dddd, HH:mm");
  if (local.isBefore(startOfYear)) return local.format("dddd, Do MMMM YY, HH:mm");
  return local.format("dddd, Do MMMM, HH:mm");
}
const fmtTs = (ts, tz = "Europe/London") => {
  if (!ts) return null;
  const m = moment.utc(ts).tz(tz);
  return m.isValid() ? m.format("D MMM 'YY, HH:mm") : null;
};

const fmtDateShort = (d) => {
  if (!d) return null;
  const m = moment(d, "YYYY-MM-DD", true);
  return m.isValid() ? m.format("MMM 'YY") : null;
};

/* ---------- Tag pills ---------- */
function TagPills({ tags = [], onRemove }) {
  const weight = (s) => (s === "severe" ? 0 : s === "warning" ? 1 : 2);
  const items = tags
    .map((t, i) => ({ t, i }))
    .sort((a, b) => {
      const d = weight(a.t.status) - weight(b.t.status);
      return d !== 0 ? d : a.i - b.i;
    })
    .map((x) => x.t);

  return (
    <div className="tags-wrap">
      {items.map((t, i) => {
        const cls = severityToClass(t.status);
        const isUser =
          (t.type || "").toLowerCase() === "user" ||
          (t.source || "").toLowerCase() === "user";
        const title = [t.label, t.reason, t.expires_at ? `until ${t.expires_at}` : null]
          .filter(Boolean)
          .join(" • ");

        return (
          <span
            key={`${t.key || t.label || "tag"}-${i}`}
            className={`status-pill ${cls} status-pill--tag`}
            title={title}
          >
            {t.label || t.key}
            {isUser && typeof onRemove === "function" && (
              <button
                className="pill-x"
                type="button"
                aria-label={`Remove ${t.label || t.key}`}
                title={`Remove ${t.label || t.key}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(t);
                }}
              >
                ×
              </button>
            )}
          </span>
        );
      })}
    </div>
  );
}

const AddTagPill = React.forwardRef(function AddTagPill({ onClick }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-haspopup="dialog"
      title="Add Tag"
      className="status-pill status-pill--tag"
      style={{
        borderStyle: "dashed",
        borderColor: "rgba(0,0,0,0.45)",
        background: "#fff",
        fontWeight: 600,
      }}
    >
      + Add Tag
    </button>
  );
});

/* ---------- Tag popover ---------- */
function TagPopover({ open, anchorRef, onClose, items = [], onPick, onAddNew }) {
  const popRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, w: 320, visible: false });

  useLayoutEffect(() => {
    if (!open) return;
    const a = anchorRef?.current;
    if (!a) return;

    const r = a.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 12;
    const w = Math.min(Math.max(280, r.width + 80), vw - margin * 2);
    const spaceAbove = r.top;
    const spaceBelow = vh - r.bottom;
    const preferTop = spaceAbove > spaceBelow;

    const left = Math.min(vw - w - margin, Math.max(margin, r.right - w));
    let top = preferTop ? r.top - 8 : r.bottom + 8;
    setPos({ top, left, w, visible: false });

    requestAnimationFrame(() => {
      const p = popRef.current;
      const h = p ? p.offsetHeight : 0;
      const finalTop = preferTop ? Math.max(margin, top - h) : Math.min(vh - h - margin, top);
      setPos({ top: finalTop, left, w, visible: true });
    });
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => e.key === "Escape" && onClose?.();
    const onClickAway = (e) => {
      const a = anchorRef?.current;
      if (popRef.current?.contains(e.target) || a?.contains(e.target)) return;
      onClose?.();
    };
    window.addEventListener("keydown", onEsc);
    document.addEventListener("mousedown", onClickAway, true);
    return () => {
      window.removeEventListener("keydown", onEsc);
      document.removeEventListener("mousedown", onClickAway, true);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  return createPortal(
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "transparent", zIndex: 1040 }} />
      <div
        ref={popRef}
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed",
          top: pos.top,
          left: pos.left,
          width: pos.w,
          maxWidth: "92vw",
          zIndex: 1055,
          opacity: pos.visible ? 1 : 0,
          background: "#fff",
          border: "1px solid #e6e6e6",
          borderRadius: 12,
          padding: 12,
          boxShadow: "0 12px 28px rgba(0,0,0,0.18)",
        }}
      >
        <div className="mb-2 d-flex align-items-center justify-content-between">
          <strong>Select a tag</strong>
        </div>

        <div className="tags-wrap">
          {items.map((t) => {
            const cls = severityToClass(t.default_status || "normal");
            return (
              <button
                key={t.key}
                type="button"
                className={`status-pill status-pill--tag ${cls}`}
                onClick={() => onPick?.(t)}
                title={t.label || t.key}
              >
                {t.label || t.key}
              </button>
            );
          })}
          <button
            type="button"
            onClick={onAddNew}
            title="Create a new tag"
            className="status-pill status-pill--tag"
            style={{
              borderStyle: "dashed",
              borderColor: "rgba(0,0,0,0.45)",
              background: "#fff",
              fontWeight: 600,
            }}
          >
            + New Tag
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}

/* ---------- New tag modal ---------- */
function NewTagModal({ open, onClose, vrm, onCreated }) {
  const [label, setLabel] = useState("");
  const [severity, setSeverity] = useState("normal");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLabel("");
    setSeverity("normal");
    setBusy(false);
  }, [open]);

  const key = normalizeKey(label);
  const canCreate = label.trim().length >= 2;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate) return;
    setBusy(true);
    try {
      await createUserTag({ key, label: label.trim(), default_status: severity });
      await addUserTagToVehicle(vrm, { tagKey: key, status: severity });
      onCreated?.();
    } catch (err) {
      alert("Failed to create/assign tag: " + err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const S = {
    backdrop: { position: "fixed", inset: 0, background: "rgba(17,17,17,0.45)", zIndex: 20000 },
    card: {
      position: "fixed",
      zIndex: 20001,
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: 560,
      maxWidth: "92vw",
      background: "#fff",
      borderRadius: 16,
      boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
      overflow: "hidden",
    },
    header: {
      padding: "16px 18px",
      borderBottom: "1px solid #f0f0f0",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    close: { border: 0, background: "transparent", fontSize: 22, lineHeight: 1, padding: 2, cursor: "pointer" },
    body: { padding: 18 },
    label: { fontWeight: 700, marginBottom: 8, fontSize: 18 },
    input: { padding: "12px 14px", borderRadius: 12, border: "1px solid #ddd" },
    footerBtn: { marginTop: 18, padding: "12px 16px", borderRadius: 12 },
    segWrap: { display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap" },
    segBtn: (active, color) => ({
      position: "relative",
      border: active ? `2px solid ${color}` : "2px solid rgba(0,0,0,0.18)",
      borderRadius: 14,
      padding: 0,
      background: "transparent",
      cursor: "pointer",
      transition: "transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease",
      boxShadow: active ? `0 0 0 4px ${color}66` : "0 1px 2px rgba(0,0,0,0.06)",
      transform: active ? "translateY(-1px)" : "none",
      outline: active ? `2px solid ${color}` : "none",
      outlineOffset: active ? 2 : 0,
    }),
    chip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "10px 18px",
      borderRadius: 12,
      fontWeight: 700,
      fontSize: 16,
    },
  };

  const SegBtn = ({ kind, label, onClick, active }) => {
    const map = {
      normal: { cls: "ok", color: "#9ca3af" },
      warning: { cls: "unk", color: "#e8bb06" },
      severe: { cls: "bad", color: "#cc1524" },
    };
    const conf = map[kind];
    return (
      <button
        type="button"
        onClick={onClick}
        style={S.segBtn(active, conf.color)}
        role="radio"
        aria-checked={active}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            onClick?.();
          }
        }}
      >
        <span className={`status-pill ${conf.cls}`} style={S.chip}>
          {label}
        </span>
      </button>
    );
  };

  return createPortal(
    <>
      <div style={S.backdrop} onClick={onClose} />
      <div role="dialog" aria-modal="true" style={S.card}>
        <div style={S.header}>
          <div style={{ fontSize: 22, fontWeight: 800 }}>Create New Tag</div>
          <button onClick={onClose} aria-label="Close" style={S.close}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} style={S.body}>
          <div className="mb-3">
            <div style={S.label}>Label</div>
            <input
              className="form-control"
              style={S.input}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              required
            />
          </div>

          <div className="mb-2">
            <div style={S.label}>Severity</div>
            <div style={S.segWrap} role="radiogroup" aria-label="Severity">
              <SegBtn kind="normal" label="Normal" active={severity === "normal"} onClick={() => setSeverity("normal")} />
              <SegBtn kind="warning" label="Warning" active={severity === "warning"} onClick={() => setSeverity("warning")} />
              <SegBtn kind="severe" label="Severe" active={severity === "severe"} onClick={() => setSeverity("severe")} />
            </div>
          </div>

          <button className="btn btn-dark w-100" style={S.footerBtn} type="submit" disabled={!canCreate || busy}>
            {busy ? "Saving…" : "Create & Assign"}
          </button>
        </form>
      </div>
    </>,
    document.body
  );
}

function AddAlertModal({ open, onClose, onAdd, types }) {
  const [note, setNote] = useState("");
  const [typeKey, setTypeKey] = useState("");
  const [busy, setBusy] = useState(false);

  const hasTypes = Array.isArray(types) && types.length >= 1;

  useEffect(() => {
    if (!open) return;
    setNote("");
    setBusy(false);
    setTypeKey(hasTypes && types.length === 1 ? (types[0]?.key || "") : "");
  }, [open, hasTypes, types]);

  if (!open) return null;

  const S = {
    backdrop: { position: "fixed", inset: 0, background: "rgba(17,17,17,0.45)", zIndex: 20000 },
    card: { position: "fixed", zIndex: 20001, top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 560, maxWidth: "92vw", background: "#fff", borderRadius: 16, boxShadow: "0 24px 64px rgba(0,0,0,0.35)", overflow: "hidden" },
    header: { padding: "16px 18px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "space-between" },
    close: { border: 0, background: "transparent", fontSize: 22, lineHeight: 1, padding: 2, cursor: "pointer" },
    body: { padding: 18 },
    label: { fontWeight: 700, marginBottom: 8, fontSize: 18 },
    input: { padding: "10px 12px", borderRadius: 12, border: "1px solid #ddd", minHeight: 44 },
    footer: { display: "flex", gap: 10, marginTop: 16 },
  };

  const canAdd = !busy && (!hasTypes || !!typeKey);

  return createPortal(
    <>
      <div style={S.backdrop} onClick={onClose} />
      <div role="dialog" aria-modal="true" style={S.card}>
        <div style={S.header}>
          <div style={{ fontSize: 22, fontWeight: 800 }}>Add to Alert List</div>
          <button onClick={onClose} aria-label="Close" style={S.close}>×</button>
        </div>
        <div style={S.body}>
          {hasTypes && (
            <div className="mb-2">
              <div style={S.label}>Type (required)</div>
              <select
                className="form-select"
                value={typeKey}
                onChange={(e) => setTypeKey(e.target.value)}
              >
                <option value="">Select type…</option>
                {types.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </div>
          )}

          <div className="mb-2">
            <div style={S.label}>Note (optional)</div>
            <textarea
              className="form-control"
              style={S.input}
              rows={3}
              placeholder="Why is this vehicle being flagged?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          <div style={S.footer}>
            <button type="button" className="btn btn-outline-secondary w-50" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-dark w-50"
              onClick={async () => {
                try {
                  setBusy(true);
                  await onAdd(note, typeKey || null);
                } finally {
                  setBusy(false);
                }
              }}
              disabled={!canAdd}
            >
              {busy ? "Adding…" : "Add"}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

/* ---------- Media viewer ---------- */
function MediaViewer({ imgSrc, videoUrl }) {
  const hasVideo = !!videoUrl;
  const [mode, setMode] = useState(hasVideo ? "video" : "image");
  const [videoErrored, setVideoErrored] = useState(false);

  useEffect(() => {
    setVideoErrored(false);
    setMode(hasVideo ? "video" : "image");
  }, [videoUrl, hasVideo]);

  const showVideo = hasVideo && !videoErrored && mode === "video";

  return (
    <div className="card mb-3 shadow-sm">
      <div className="media-shell position-relative">
        {hasVideo && (
          <ul className="nav nav-pills media-tabs-overlay" role="tablist">
            <li className="nav-item">
              <button type="button" className={`nav-link ${showVideo ? "active" : ""}`} onClick={() => setMode("video")}>
                Video
              </button>
            </li>
            <li className="nav-item">
              <button type="button" className={`nav-link ${!showVideo ? "active" : ""}`} onClick={() => setMode("image")}>
                Image
              </button>
            </li>
          </ul>
        )}

        {hasVideo && !videoErrored && (
          <video
            key={videoUrl}
            className={`big-image media-layer ${showVideo ? "is-active" : "is-inactive"} rounded-3`}
            src={videoUrl}
            poster={imgSrc}
            loop
            muted
            autoPlay
            controls
            playsInline
            preload="metadata"
            onError={() => setVideoErrored(true)}
            style={{ backgroundColor: "#000" }}
          />
        )}

        <img
          className={`big-image media-layer ${showVideo ? "is-inactive" : "is-active"} rounded-3`}
          src={imgSrc}
          alt="Captured"
          loading="eager"
          decoding="async"
          fetchpriority="high"
        />
      </div>
    </div>
  );
}

function SimpleMotHistory({ vrm }) {
  const [tests, setTests] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [openSet, setOpenSet] = React.useState(() => new Set());

  React.useEffect(() => {
    let ok = true;

    const load = async () => {
      setLoading(true);
      try {
        const rows = await fetchMotTests(vrm);
        if (!ok) return;
        setTests(rows);
      } catch {
        if (!ok) return;
        setTests([]);
      } finally {
        if (ok) setLoading(false);
      }
    };

    if (vrm) load();

    const onOnline = () => ok && load();
    const onVis = () => {
      if (document.visibilityState === "visible" && ok) load();
    };

    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVis);

    return () => {
      ok = false;
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [vrm]);

  const fmtDate = (d) => {
    if (!d) return "—";
    const m = moment(d, moment.ISO_8601, true);
    return (m.isValid() ? m : moment(d)).format("D MMM 'YY");
  };

  const fmtOdo = (mi, km) => {
    const nMi = Number(String(mi ?? "").replace(/,/g, ""));
    const nKm = Number(String(km ?? "").replace(/,/g, ""));
    if (Number.isFinite(nMi)) return `${nMi.toLocaleString("en-GB")} mi`;
    if (Number.isFinite(nKm)) return `${nKm.toLocaleString("en-GB")} km`;
    return null;
  };

  const toggle = (key) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const S = {
    wrap: { marginTop: 16 },
    header: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 },
    row: { padding: "8px 0", borderBottom: "1px solid #f4f4f4" },
    lineWrap: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 },
    lineText: { fontSize: 13, lineHeight: 1.35, color: "#111", textAlign: "left", flex: "1 1 auto", minWidth: 0 },
    toggleLink: { border: 0, background: "transparent", padding: 0, cursor: "pointer", fontSize: 12, color: "#555" },
    defects: { marginTop: 6, marginLeft: 5 },
    defectLine: { fontSize: 12, lineHeight: 1.35, textAlign: "left", color: "#444" },
    meta: { color: "#666" },
    resultPill: { display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 9999, fontWeight: 700, letterSpacing: "0.02em", lineHeight: 1 },
    resultPass: { background: "#93d196ff", color: "#fff" },
    resultFail: { background: "#cd6c74ff", color: "#fff" },
    resultOther: { background: "#efefef", color: "#111" },
    pillCell: { display: "inline-block", width: 60, textAlign: "center" },
  };

  const norm = (s) => String(s || "").trim().toLowerCase();

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <strong>MOT History</strong>
        <span className="text-muted small">
          {loading ? "Loading…" : `${tests.length} ${tests.length === 1 ? "test" : "tests"}`}
        </span>
      </div>

      {loading ? null : tests.length === 0 ? (
        <div className="text-muted small" style={{ textAlign: "left" }}>
          No MOT history found.
        </div>
      ) : (
        <div role="region" aria-label="MOT history">
          {tests.map((t, idx) => {
            const dateStr = fmtDate(t.test_date);
            const expStr = t.expiry_date ? ` • Exp: ${fmtDate(t.expiry_date)}` : "";
            const odoStr = fmtOdo(t.odometer_mi, t.odometer_km);

            const defects = Array.isArray(t.defects) ? t.defects : [];
            const key = t.id ?? `${t.test_date || "na"}-${t.result || "na"}-${idx}`;
            const isOpen = openSet.has(key);
            const hasDefects = defects.length > 0;

            const r = norm(t.result);
            const isPass = r === "pass" || r === "passed" || r === "prs";
            const isFail = r === "fail" || r === "failed";
            const pillStyle = { ...S.resultPill, ...(isPass ? S.resultPass : isFail ? S.resultFail : S.resultOther) };
            const resultLabel = (isPass && "PASS") || (isFail && "FAIL") || String(t.result || "").toUpperCase();

            return (
              <div key={key} style={S.row}>
                <div style={S.lineWrap}>
                  <div style={S.lineText}>
                    <span style={S.pillCell}>
                      <span style={pillStyle} aria-label={`MOT ${resultLabel.toLowerCase()}`}>{resultLabel}</span>
                    </span>
                    <span style={{ marginLeft: 2 }}>{dateStr}</span>
                    {expStr && <span>{expStr}</span>}
                    {odoStr && <span className="text-muted" style={{ marginLeft: 8 }}>({odoStr})</span>}
                  </div>

                  {hasDefects && (
                    <button
                      type="button"
                      style={S.toggleLink}
                      onClick={() => toggle(key)}
                      aria-expanded={isOpen}
                      aria-controls={`def-${key}`}
                    >
                      {isOpen ? "less" : "more"}
                    </button>
                  )}
                </div>

                {hasDefects && isOpen && (
                  <div id={`def-${key}`} style={S.defects}>
                    {defects.map((d, i) => (
                      <div key={i} style={S.defectLine}>
                        <span style={{ fontWeight: 600 }}>{d.severity}</span>
                        {": "}
                        <span>{d.text}</span>
                        {d.ref_code ? <span style={S.meta}> ({d.ref_code})</span> : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* --------------------------------- Detail -------------------------------- */
export default function Detail() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  // Seed from navigation state if present (may be partial)
  const seeded = (location.state && typeof location.state === "object") ? location.state : {};
  const derivedId =
    params?.id ||
    seeded?.id ||
    seeded?.hitId ||
    seeded?.public_ulid ||
    null;

  const missingId = !derivedId;

  const [item, setItem] = useState(seeded?.vrm ? seeded : null);
  const [, setLoading] = useState(!seeded?.vrm);

  // Hydrate by ID if we don't have everything we need
  useEffect(() => {
    let alive = true;

    async function hydrate() {
      if (!derivedId) return; // no ID available
      const needs =
        !item ||
        item?.video_url == null ||               // <-- treat null OR undefined as missing
        !item.make ||
        !item.colour ||
        !item.monthOfFirstRegistration ||
        !item.motStatus ||
        !item.taxStatus ||
        !item.firstSeen ||
        !item.lastSeen ||
        !item.hitTime ||
        !item.taxDueDate ||
        !item.motExpiryDate ||
        !item.engineCapacity ||
        !item.co2Emissions ||
        !item.dateOfLastV5CIssue;

      if (!needs) return;

      setLoading(true);
      try {
        const full = await fetchHitById(derivedId);
        if (!alive) return;
        setItem((prev) => ({ ...(prev || {}), ...(full || {}) }));
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setLoading(false);
      }
    }

    hydrate();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedId]);

  const img_source = `${API}/getFullImage?hit_id=${encodeURIComponent(derivedId || "")}`;
  const dir = (item?.direction || "").toLowerCase();
  const isForward = dir.includes("forward") || dir.includes("front");
  const isReverse = dir.includes("reverse") || dir.includes("back");

  const m = item?.hitTime ? moment.utc(item.hitTime).tz("Europe/London") : null;
  const f = item?.firstSeen ? moment.utc(item.firstSeen).tz("Europe/London") : null;
  const l = item?.lastSeen ? moment.utc(item.lastSeen).tz("Europe/London") : null;

  const formattedDate = m?.isValid() ? m.format("dddd, Do MMMM YYYY") : "—";
  const formattedTime = m?.isValid() ? m.format("HH:mm:ss") : "—";
  const formattedFirstDateTime = f?.isValid() ? prettyRecentDate(f) : "—";
  const formattedLastDateTime = l?.isValid() ? prettyRecentDate(l) : "—";

  const [hitData, setHitData] = useState([]);

  let startDate = moment.utc().subtract(1, "year").month(0).date(1).startOf("day").format("YYYY-MM-DD HH:mm:ss");
  let endDate = moment.utc().add(1, "day").startOf("day").format("YYYY-MM-DD HH:mm:ss");

  const res = logos.filter((x) => x.name.toUpperCase() === (item?.make || "").toUpperCase());
  const showLogo = res.length > 0 ? <img className="logo" src={res[0].image.optimized} alt="make logo" /> : null;

  useEffect(() => {
    if (!item?.vrm) return;
    getHitCount(startDate, endDate, item.vrm).then((info) => setHitData(info.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.vrm]);

  const handleCalendarDayClick = (datum) => {
    const v = Number(datum?.value);
    if (!Number.isFinite(v) || v <= 0) return;
    const dayStr = datum?.day || (datum?.date ? moment(datum.date).format("YYYY-MM-DD") : null);
    if (!dayStr) return;
    const startLocal = moment.tz(dayStr, "YYYY-MM-DD", "Europe/London").startOf("day");
    const endLocal = startLocal.clone().endOf("day");
    navigate("/", {
      state: {
        vrm: item?.vrm,
        page: 1,
        startDateTime: startLocal.toDate(),
        endDateTime: endLocal.toDate(),
        source: "calendar",
        restoreOnBack: true,
      },
    });
  };

  /* ----- Tags ----- */
  const [tags, setTags] = useState(item?.tags || []);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const addBtnRef = useRef(null);
  const [library, setLibrary] = useState([]);

  const refreshTags = async () => {
    try {
      if (!item?.vrm) return;
      setTags(await fetchVehicleTags(item.vrm));
    } catch { }
  };

  useEffect(() => {
    let ok = true;
    if (!item?.vrm) return () => { };
    fetchVehicleTags(item.vrm)
      .then((t) => ok && setTags(t))
      .catch(() => { });
    return () => { ok = false; };
  }, [item?.vrm]);

  const assignedKeys = useMemo(
    () =>
      new Set(
        (tags || [])
          .map((t) => (t.key || t.label || "").toLowerCase())
          .filter(Boolean)
      ),
    [tags]
  );

  useEffect(() => {
    if (!popoverOpen) return;
    let ok = true;
    (async () => {
      try {
        const all = await listUserTags();
        if (!ok) return;
        setLibrary(all.filter((t) => !assignedKeys.has((t.key || "").toLowerCase())));
      } catch { }
    })();
    return () => { ok = false; };
  }, [popoverOpen, assignedKeys]);

  const handleRemove = async (t) => {
    const key = (t.key || t.label || "").toLowerCase();
    if (!key || !item?.vrm) return;
    setTags((prev) => prev.filter((x) => (x.key || x.label || "").toLowerCase() !== key));
    try {
      await removeUserTagFromVehicle(item.vrm, t.key || t.label);
      await refreshTags();
    } catch (e) {
      alert("Failed to remove tag: " + e.message);
      try { await refreshTags(); } catch { }
    }
  };

  /* ----- Alert list (note + type) ----- */
  const [alertInfo, setAlertInfo] = useState(() => {
    const a = item?.alert;
    if (a && typeof a.on_list === "boolean") return a;
    return {
      on_list: !!item?.isAlert,
      note: null,
      added_at: null,
      expires_at: null,
      make: null,
      colour: null,
      type_key: null,
      type_label: null,
      type_color_hex: null,
    };
  });
  const [onAlertList, setOnAlertList] = useState(false);
  const [addAlertOpen, setAddAlertOpen] = useState(false);

  const [types, setTypes] = useState(getAlertTypesSnapshot());
  useEffect(() => {
    let ok = true;
    refreshAlertTypes({ force: true }).then((ts) => ok && setTypes(ts));
    const onB = () => setTypes(getAlertTypesSnapshot());
    window.addEventListener("alerttypes-updated", onB);
    return () => window.removeEventListener("alerttypes-updated", onB);
  }, []);

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        if (!item?.vrm) return;
        const flag = await checkAlertVehicle(item.vrm);
        if (!ok) return;
        setOnAlertList(flag);
        if (flag) {
          try {
            const res = await listAlertVehicles(item.vrm, 1, 10);
            const exact = (res.rows || []).find(
              (r) => (r.vrm || "").toUpperCase() === String(item.vrm || "").toUpperCase()
            );
            setAlertInfo((prev) => ({
              ...prev,
              note: exact?.note ?? null,
              type_key: exact?.type_key ?? prev.type_key ?? item?.alert?.type_key ?? null,
              type_label: exact?.type_label ?? prev.type_label ?? item?.alert?.type_label ?? null,
              type_color_hex: exact?.type_color_hex ?? prev.type_color_hex ?? item?.alert?.type_color_hex ?? null,
            }));
          } catch { }
        } else {
          setAlertInfo((prev) => ({ ...prev, note: null }));
        }
      } catch { }
    })();
    return () => { ok = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.vrm]);

  async function addToAlertWithNote(note, typeKey) {
    try {
      await addAlertVehicle(item.vrm, {
        make: item.make,
        colour: item.colour,
        note: note?.trim() || undefined,
        ...(typeKey ? { typeKey } : {}),
      });

      const meta = typeKey ? (types.find(t => t.key === typeKey) || null) : null;
      setOnAlertList(true);
      setAlertInfo(prev => ({
        ...prev,
        note: note?.trim() || null,
        type_key: typeKey || null,
        type_label: meta?.label || (prev.type_key === typeKey ? prev.type_label : null),
        type_color_hex: meta?.color_hex || (prev.type_key === typeKey ? prev.type_color_hex : null),
      }));
      setAddAlertOpen(false);

      try {
        const res = await listAlertVehicles(item.vrm, 1, 10);
        const exact = (res.rows || []).find(
          r => (r.vrm || "").toUpperCase() === String(item.vrm || "").toUpperCase()
        );
        if (exact) {
          setAlertInfo(prev => ({
            ...prev,
            note: exact.note ?? prev.note ?? null,
            type_key: exact.type_key ?? prev.type_key ?? null,
            type_label: exact.type_label ?? prev.type_label ?? null,
            type_color_hex: exact.type_color_hex ?? prev.type_color_hex ?? null,
          }));
        }
      } catch { }
    } catch (e) {
      alert(`Failed to add to alert list: ${e.message}`);
    }
  }

  async function handleAlertToggle() {
    if (!item?.vrm) return;
    if (onAlertList) {
      try {
        await removeAlertVehicle(item.vrm);
        setOnAlertList(false);
        setAlertInfo((prev) => ({
          ...prev,
          note: null,
          type_key: null,
          type_label: null,
          type_color_hex: null,
        }));
      } catch (e) {
        alert(`Failed to remove from alert list: ${e.message}`);
      }
    } else {
      setAddAlertOpen(true);
    }
  }

  const [editMode, setEditMode] = useState(false);
  const [editNote, setEditNote] = useState("");
  const [editTypeKey, setEditTypeKey] = useState("");

  useEffect(() => {
    if (!editMode) return;
    const currentType = alertInfo?.type_key || "";
    const onlyType = Array.isArray(types) && types.length === 1 ? types[0].key : "";
    setEditNote(alertInfo?.note || "");
    setEditTypeKey(currentType || onlyType);
  }, [editMode, alertInfo, types]);

  const requireType = Array.isArray(types) && types.length >= 1;
  const canSave = !requireType || !!editTypeKey;

  const saveAlertEdits = async () => {
    if (!item?.vrm) return;
    if (requireType && !editTypeKey) return;
    try {
      // Use the credentialled helper from data.js
      await updateAlertVehicle(item.vrm, {
        note: editNote.trim(),
        ...(requireType ? { typeKey: editTypeKey || null } : {}),
      });
      const meta = requireType ? (types.find(t => t.key === editTypeKey) || null) : null;
      setAlertInfo(prev => ({
        ...prev,
        note: editNote.trim() || null,
        type_key: requireType ? (editTypeKey || null) : null,
        type_label: meta?.label ?? prev.type_label,
        type_color_hex: meta?.color_hex ?? prev.type_color_hex,
      }));
      setEditMode(false);

      try {
        const res2 = await listAlertVehicles(item.vrm, 1, 10);
        const exact = (res2.rows || []).find(
          r => (r.vrm || "").toUpperCase() === String(item.vrm || "").toUpperCase()
        );
        if (exact) {
          setAlertInfo(prev => ({
            ...prev,
            note: exact.note ?? prev.note ?? null,
            type_key: exact.type_key ?? prev.type_key ?? null,
            type_label: exact.type_label ?? prev.type_label ?? null,
            type_color_hex: exact.type_color_hex ?? prev.type_color_hex ?? null,
          }));
        }
      } catch { }
    } catch (e) {
      alert(e?.message || "Update failed");
    }
  };

  // ---------- RENDER ----------
  return (
    <div className="App">
      <div className="container">
        {missingId ? (
          <div className="alert alert-warning mt-3">
            Missing hit identifier.{" "}
            <button className="btn btn-link p-0" onClick={() => navigate(-1)}>Go back</button>
          </div>
        ) : (
          <>
            <div className="row">
              {/* LEFT: media + calendar */}
              <div className="col-8">
                <MediaViewer imgSrc={img_source} videoUrl={item?.video_url} />

                <div className="card shadow-sm">
                  <div className="card-header d-flex align-items-center justify-content-between">
                    <strong>History</strong>
                  </div>
                  <div className="card-body p-0">
                    <div className="calendar-container">
                      <ResponsiveCalendar
                        data={hitData}
                        from={startDate}
                        to={endDate}
                        emptyColor="#f3f4f6"
                        colors={["#e5e5e5", "#bfbfbf", "#8c8c8c", "#4d4d4d"]}
                        margin={{ top: 10, right: 20, bottom: 10, left: 40 }}
                        yearSpacing={40}
                        monthBorderColor="#ffffff"
                        dayBorderWidth={2}
                        dayBorderColor="#ffffff"
                        onClick={handleCalendarDayClick}
                        theme={{ tooltip: { container: { background: "#fff" } } }}
                        legends={[
                          {
                            anchor: "bottom-right",
                            direction: "row",
                            translateY: 36,
                            itemCount: 4,
                            itemWidth: 42,
                            itemHeight: 36,
                            itemsSpacing: 14,
                            itemDirection: "right-to-left",
                          },
                        ]}
                        tooltip={(d) => {
                          const day = moment.tz(d.day, "YYYY-MM-DD", "Europe/London");
                          const v = Number(d.value) || 0;
                          return (
                            <div
                              style={{
                                background: "#ffffff",
                                color: "#111",
                                padding: "8px 10px",
                                borderRadius: 8,
                                boxShadow: "0 4px 18px rgba(0,0,0,0.15)",
                                border: `1px solid ${d.color}`,
                                lineHeight: 1.2,
                                fontSize: 12,
                                maxWidth: 220,
                                pointerEvents: "none",
                              }}
                            >
                              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                {day.format("dddd, D MMMM YYYY")}
                              </div>
                              <div>{v.toLocaleString("en-GB")} {v === 1 ? "hit" : "hits"}</div>
                            </div>
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT: details + tags */}
              <div className="col">
                <div className="card sticky-top shadow-sm">
                  <div className="card-body">
                    <div className={`veh-header ${showLogo ? "has-logo" : "no-logo"}`}>
                      <div className="veh-plate-slot">
                        <span className={isForward ? "registration-ui-front-big" : "registration-ui-rear-big"}>
                          {item?.vrm}
                        </span>
                      </div>
                      {showLogo && <div className="veh-logo-slot">{showLogo}</div>}
                    </div>

                    <div className="info-container" style={{ marginTop: 32 }}>
                      <p className="detail-content">
                        <img className="icon" src={direction} alt="direction" />
                        {isForward ? "Inbound" : isReverse ? "Outbound" : "Unknown"}
                      </p>
                    </div>

                    <div className="info-container">
                      <p className="detail-content">
                        <img className="icon" src={calendar} alt="date" />
                        {formattedDate}
                      </p>
                      <p className="detail-content">
                        <img className="icon" src={clock} alt="time" />
                        {formattedTime}
                      </p>
                    </div>

                    <div className="info-container">
                      <p className="detail-content">
                        <img className="icon" src={car} alt="make" />
                        {item?.make}
                      </p>
                      <p className="detail-content">
                        <img className="icon" src={paint} alt="colour" />
                        {capitalizeFirstLetter(item?.colour?.toLowerCase())}
                      </p>
                    </div>

                    <div className="info-container">
                      <p className="detail-content">
                        <img className="icon" src={crane} alt="first reg" />
                        {getFirstReg(item?.monthOfFirstRegistration)}
                      </p>
                      <p className="detail-content">
                        <img className="icon" src={age} alt="age" />
                        {getAge(item?.monthOfFirstRegistration)}
                      </p>
                    </div>

                    <div className="info-container">
                      <p className="detail-content">
                        <img className="icon" src={engine} alt="MOT" />
                        MOT {item?.motStatus}
                        {item?.motExpiryDate ? (
                          <span className="text-muted"> (Expires {fmtDateShort(item.motExpiryDate)})</span>
                        ) : null}
                      </p>
                      <p className="detail-content">
                        <img className="icon" src={tax} alt="Tax" />
                        {item?.taxStatus}
                        {item?.taxDueDate ? (
                          <span className="text-muted"> (Expires {fmtDateShort(item.taxDueDate)})</span>
                        ) : null}
                      </p>
                    </div>

                    <div className="info-container">
                      <p className="detail-content">
                        <img className="icon" src={firstSeen} alt="First Seen" />
                        {formattedFirstDateTime}
                      </p>
                      <p className="detail-content">
                        <img className="icon" src={lastSeen} alt="Last Seen" />
                        {formattedLastDateTime}
                      </p>
                    </div>

                    <div className="info-container">
                      {item.engineCapacity > 0 ? (
                        <p className="detail-content">
                          <img className="icon" src={cylinder} alt="Engine capacity" />
                          {item?.engineCapacity ? `${item.engineCapacity} cc` : "-"}
                        </p>
                      ) : null}
                      {item.co2Emissions > 0 ? (
                        <p className="detail-content">
                          <img className="icon" src={emission} alt="CO₂ emissions" />
                          {item?.co2Emissions ? `${item.co2Emissions} g/km` : "-"}
                        </p>
                      ) : null}
                    </div>

                    {item.dateOfLastV5CIssue ? (

                      <div className="info-container">
                        <p className="detail-content">
                          <img className="icon" src={lastV5} alt="V5C issued" />
                          {item?.dateOfLastV5CIssue ? `Last V5C: ${fmtDateShort(item.dateOfLastV5CIssue)}` : "Last V5C: -"}
                        </p>
                      </div>
                    ) : null}

                    <div className="info-container mt-3" style={{ marginBottom: 16 }}>
                      <button
                        type="button"
                        className="btn btn-dark w-100 show-all-btn"
                        onClick={() => navigate("/", { state: { vrm: item?.vrm, page: 1, startDateTime: null, endDateTime: null } })}
                      >
                        Show All Hits
                      </button>
                    </div>

                    <hr className="card-divider" aria-hidden="true" />

                    {/* Alert List */}
                    <div className="info-container mt-2" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <strong>Alert List</strong>
                      <div className="d-flex gap-2">
                        {onAlertList && !editMode && (
                          <button type="button" className="btn btn-outline-secondary" onClick={() => setEditMode(true)}>
                            Edit
                          </button>
                        )}
                        <button
                          type="button"
                          className={`btn ${onAlertList ? "btn-outline-danger" : "btn-danger"}`}
                          onClick={handleAlertToggle}
                          title={onAlertList ? "Remove from Alert List" : "Add to Alert List"}
                        >
                          {onAlertList ? "Remove from Alert List" : "Add to Alert List"}
                        </button>
                      </div>
                    </div>

                    <div className="mt-2" style={{ textAlign: "left" }}>
                      {onAlertList ? (
                        editMode ? (
                          <div className="border rounded p-2">
                            {types.length >= 1 && (
                              <div className="mb-2">
                                <label className="form-label">Type (required)</label>
                                <select
                                  className="form-select"
                                  value={editTypeKey || ""}
                                  onChange={(e) => setEditTypeKey(e.target.value)}
                                >
                                  <option value="">Select type…</option>
                                  {types.map((t) => (
                                    <option key={t.key} value={t.key}>
                                      {t.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}

                            <div className="mb-2">
                              <label className="form-label">Note</label>
                              <textarea
                                className="form-control"
                                rows={3}
                                value={editNote}
                                onChange={(e) => setEditNote(e.target.value)}
                              />
                            </div>

                            <div className="d-flex gap-2 justify-content-end">
                              <button className="btn btn-outline-secondary" onClick={() => setEditMode(false)}>
                                Cancel
                              </button>
                              <button className="btn btn-dark" onClick={saveAlertEdits} disabled={!canSave}>
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="alert alert-danger py-2 px-3 mb-2" role="status">
                            <div style={{ fontWeight: 700 }}>This vehicle is on your alert list.</div>
                            {alertInfo?.note ? (
                              <div className="mt-1">{alertInfo.note}</div>
                            ) : (
                              <div className="mt-1 text-muted">No note recorded for this vehicle.</div>
                            )}
                            {types.length >= 1 && alertInfo?.type_label && (
                              <div className="mt-2">
                                <span
                                  className="badge rounded-pill"
                                  style={{ backgroundColor: alertInfo.type_color_hex || "#444", color: "#fff", fontWeight: 700 }}
                                  title={alertInfo.type_label}
                                >
                                  {alertInfo.type_label}
                                </span>
                              </div>
                            )}
                            <div className="mt-1 small text-muted">
                              {fmtTs(alertInfo?.added_at) ? `Added: ${fmtTs(alertInfo.added_at)}` : null}
                              {fmtTs(alertInfo?.added_at) && alertInfo?.expires_at ? " • " : null}
                              {fmtTs(alertInfo?.expires_at) ? `Expires: ${fmtTs(alertInfo.expires_at)}` : null}
                            </div>
                          </div>
                        )
                      ) : (
                        <div className="text-muted small">This vehicle is not on your alert list.</div>
                      )}
                    </div>

                    <hr className="card-divider" aria-hidden="true" />

                    {/* Tags */}
                    <div className="info-container mt-3" style={{ marginTop: 16 }}>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <strong>Tags</strong>
                        <span className="text-muted small">{(tags?.length ?? 0)} active</span>
                      </div>

                      <TagPills tags={tags} onRemove={handleRemove} />

                      <div className="tags-wrap mt-2" style={{ marginBottom: 16 }}>
                        <AddTagPill ref={addBtnRef} onClick={() => setPopoverOpen(true)} />
                      </div>
                    </div>

                    <hr className="card-divider" aria-hidden="true" />

                    <div style={{ marginTop: 16 }}>
                      <SimpleMotHistory vrm={item?.vrm} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Popover & Modals (siblings of .row, wrapped by fragment) */}
            <TagPopover
              open={popoverOpen}
              anchorRef={addBtnRef}
              items={library}
              onClose={() => setPopoverOpen(false)}
              onPick={async (t) => {
                try {
                  if (!item?.vrm) return;
                  await addUserTagToVehicle(item.vrm, { tagKey: t.key, status: t.default_status });
                } catch (e) {
                  alert("Failed to assign tag: " + e.message);
                } finally {
                  setPopoverOpen(false);
                  await fetchVehicleTags(item?.vrm).then(setTags).catch(() => { });
                }
              }}
              onAddNew={() => {
                setPopoverOpen(false);
                setNewOpen(true);
              }}
            />

            <NewTagModal
              open={newOpen}
              onClose={() => setNewOpen(false)}
              vrm={item?.vrm}
              onCreated={async () => {
                setNewOpen(false);
                await fetchVehicleTags(item?.vrm).then(setTags).catch(() => { });
              }}
            />

            <AddAlertModal
              open={addAlertOpen}
              onClose={() => setAddAlertOpen(false)}
              onAdd={addToAlertWithNote}
              types={types}
            />
          </>
        )}
      </div>
    </div>
  );
}
